import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Select,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  HStack,
  VStack,
  Icon,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  IconButton,
  Tooltip,
  Avatar,
} from "@chakra-ui/react";
import {
  SearchIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InfoIcon,
  CheckIcon,
  ViewIcon,
  CloseIcon,
} from "@chakra-ui/icons";
import { motion } from "framer-motion";
import { useSetRecoilState } from "recoil";
import {
  FaUser,
  FaLink,
  FaPlus,
} from "react-icons/fa";

// Custom components
import PayeeDetailsModal from "./PayeeDetailsModal";
import LinkedAccountDetailsModal from "./LinkedAccountDetailsModal";
import { payeeTableHeaders } from "../variables/columnsData";
import { toastState } from "atoms/toastState";
import logger from "infra/logger";
import useAxios from "axios-hooks";

// Motion components
const MotionBox = motion(Box);
const MotionFlex = motion(Flex);
const MotionTr = motion(Tr);

// Styled components
// Styled components
const GlassMorphismCard = ({ children, ...rest }) => {
  const bgColor = useColorModeValue(
    "rgba(255, 255, 255, 0.8)",
    "rgba(26, 32, 44, 0.8)"
  );
  const borderColor = useColorModeValue(
    "rgba(255, 255, 255, 0.5)",
    "rgba(255, 255, 255, 0.1)"
  );

  return (
    <Box
      bg={bgColor}
      backdropFilter="blur(10px)"
      borderRadius="2xl"
      boxShadow="xl"
      border="1px solid"
      borderColor={borderColor}
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{ boxShadow: "2xl" }}
      {...rest}
    >
      {children}
    </Box>
  );
};

const PaginationButton = ({ children, isActive, isDisabled, onClick }) => {
  return (
    <Button
      size="sm"
      variant={isActive ? "solid" : "outline"}
      colorScheme={isActive ? "orange" : "gray"}
      isDisabled={isDisabled}
      onClick={onClick}
      minW="40px"
      h="40px"
      borderRadius="md"
      _hover={!isDisabled && { transform: "translateY(-2px)", shadow: "md" }}
      transition="all 0.2s"
    >
      {children}
    </Button>
  );
};

const EmptyStateIllustration = () => (
  <MotionFlex
    direction="column"
    align="center"
    justify="center"
    py={12}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <Icon as={FaUser} boxSize={16} color="#f57d36" mb={4} />
    <Text fontSize="xl" fontWeight="bold" mb={2}>
      No Payees Found
    </Text>
    <Text color="gray.500" textAlign="center" maxW="md">
      There are no payees available with the selected filter. Try changing your
      filter criteria.
    </Text>
  </MotionFlex>
);

const StatusBadge = ({ status }) => {
  let color, label, bgColor;

  switch (status) {
    case "verified":
      label = "Verified";
      bgColor = "green.400";
      color = "white";
      break;
    case "rejected":
      label = "Rejected";
      bgColor = "red.400";
      color = "white";
      break;
    case "pending":
      label = "Pending";
      bgColor = "orange.400";
      color = "white";
      break;
    case "not_initiated":
      label = "Not Initiated";
      bgColor = "blue.400";
      color = "white";
      break;
    default:
      label = "N/A";
      bgColor = "gray.400";
      color = "white";
      break;
  }

  return (
    <Badge
      px={3}
      py={1}
      borderRadius="full"
      bg={bgColor}
      color={color}
      fontSize="xs"
      fontWeight="medium"
    >
      {label}
    </Badge>
  );
};

const TypeBadge = ({ type }) => {
  let color, label;

  switch (type) {
    case "property_owner":
      color = "purple";
      label = "Property Owner";
      break;
    case "admin":
      color = "blue";
      label = "Admin";
      break;
    case "standard":
      color = "teal";
      label = "Standard";
      break;
    default:
      color = "gray";
      label = "N/A";
  }

  return (
    <Badge
      px={3}
      py={1}
      borderRadius="full"
      colorScheme={color}
      variant="subtle"
      fontSize="xs"
      fontWeight="medium"
    >
      {label}
    </Badge>
  );
};

const PayeesTable = (props) => {
  const { tableData = [], onRefresh, statusFilter, setStatusFilter } = props;
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredPayees, setFilteredPayees] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(8);
  const [selectedPayee, setSelectedPayee] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [linkedAccountData, setLinkedAccountData] = useState(null);
  const [isLinkedAccountModalOpen, setIsLinkedAccountModalOpen] = useState(false);
  const [loadingActions, setLoadingActions] = useState({});

  const setToast = useSetRecoilState(toastState);
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");
  const textColor = useColorModeValue("#2D3748", "white");
  const hoverBg = useColorModeValue("#F7FAFC", "#2D3748");
  const theadBgColor = useColorModeValue("#F7FAFC", "#1A202C");
  const themeColor = "#f57d36";
  const [{ data, error, isLoading }, execute] = useAxios({
    url: ``,
    method: "PATCH",
  }, { manual: true });
  useEffect(() => {
    let filtered = tableData;

    // Filter by verification status first
    if (statusFilter && statusFilter !== "all") {
      filtered = filtered.filter(
        (payee) => payee.VerificationStatus === statusFilter
      );
    }

    // Then filter by search query
    if (searchQuery.trim() === "") {
      setFilteredPayees(filtered);
    } else {
      const lowercasedQuery = searchQuery.toLowerCase();
      const searchFiltered = filtered.filter(
        (payee) =>
          payee.FirstName?.toLowerCase().includes(lowercasedQuery) ||
          payee.LastName?.toLowerCase().includes(lowercasedQuery) ||
          payee.Email?.toLowerCase().includes(lowercasedQuery) ||
          payee.MobileNumber?.toLowerCase().includes(lowercasedQuery) ||
          payee.Role?.toLowerCase().includes(lowercasedQuery) ||
          payee.Type?.toLowerCase().includes(lowercasedQuery)
      );
      setFilteredPayees(searchFiltered);
    }
    // Reset to first page when search query or filter changes
    setCurrentPage(0);
  }, [searchQuery, tableData, statusFilter]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };
  const handleViewDetails = (payee) => {
    setSelectedPayee(payee);
    setIsDetailModalOpen(true);
  };

  const handleEditPayee = (payee) => {
    // Handle edit payee logic
    console.log("Edit payee:", payee);
    // You can add edit functionality here
  }; const handleVerifyPayee = async (payee) => {
    const actionKey = `${payee.Id}_accept`;
    setLoadingActions(prev => ({ ...prev, [actionKey]: true }));

    try {
      const response = await execute({
        url: `/api/user/verify_financials/${payee.Id}/accept`,
        method: "PATCH",
      });

      setToast({
        isOpen: true,
        title: "Success",
        status: "success",
        description: "Payee verified successfully",
      });

      logger.log("Payee verified successfully:", response.data);

      // Refresh the data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      logger.error("Error verifying payee:", error);
      setToast({
        isOpen: true,
        title: "Error",
        status: "error",
        description: error?.response?.data?.error || "Failed to verify payee",
      });
    } finally {
      setLoadingActions(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleRejectPayee = async (payee) => {
    const actionKey = `${payee.Id}_reject`;
    setLoadingActions(prev => ({ ...prev, [actionKey]: true }));

    try {
      const response = await execute({
        url: `/api/user/verify_financials/${payee.Id}/reject`,
        method: "PATCH",
      });

      setToast({
        isOpen: true,
        title: "Success",
        status: "success",
        description: "Payee rejected successfully",
      });

      logger.log("Payee rejected successfully:", response.data);

      // Refresh the data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      logger.error("Error rejecting payee:", error);
      setToast({
        isOpen: true,
        title: "Error",
        status: "error",
        description: error?.response?.data?.error || "Failed to reject payee",
      });
    } finally {
      setLoadingActions(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleCreateLinkedAccount = async (payee) => {
    const actionKey = `${payee.Id}_create_linked`;
    setLoadingActions(prev => ({ ...prev, [actionKey]: true }));

    try {
      // TODO: Implement API call to create linked account
      const response = await execute({
        url: `/api/admin/financials/payee/linkedAccount/${payee.Id}`,
        method: "POST",
      });

      setToast({
        isOpen: true,
        title: "Created",
        status: "success",
        description: "Linked account created successfully ",
      });

      logger.log("Create linked account for payee:", payee);

      // This would typically open a modal or navigate to account creation
      // if (onRefresh) {
      //   onRefresh();
      // }
    } catch (error) {
      logger.error("Error creating linked account:", error);
      setToast({
        isOpen: true,
        title: "Error",
        status: "error",
        description: error?.response?.data?.error || "Failed to create linked account",
      });
    } finally {
      setLoadingActions(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const handleViewLinkedAccount = async (payee) => {
    const actionKey = `${payee.Id}_view_linked`;
    setLoadingActions(prev => ({ ...prev, [actionKey]: true }));

    try {
      const response = await execute({
        url: `/api/admin/financials/payee/linkedAccount/${payee.Id}`,
        method: "GET",
      });
      
      if (response?.data?.data) {
        setLinkedAccountData(response.data.data);
        setIsLinkedAccountModalOpen(true);
      }
    } catch (error) {
      logger.error("Error fetching linked account:", error);
      setToast({
        isOpen: true,
        title: "Error",
        status: "error",
        description: error?.response?.data?.error || "Failed to fetch linked account details",
      });
    } finally {
      setLoadingActions(prev => ({ ...prev, [actionKey]: false }));
    }
  };

  const closeDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedPayee(null);
  };

  const closeLinkedAccountModal = () => {
    setIsLinkedAccountModalOpen(false);
    setLinkedAccountData(null);
  };

  // Pagination logic
  const pageCount = Math.ceil(filteredPayees.length / pageSize);
  const paginatedData = React.useMemo(() => {
    const start = currentPage * pageSize;
    const end = start + pageSize;
    return filteredPayees.slice(start, end);
  }, [filteredPayees, currentPage, pageSize]);

  const gotoPage = (page) => {
    setCurrentPage(page);
  };

  const nextPage = () => {
    if (currentPage < pageCount - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const previousPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const canPreviousPage = currentPage > 0;
  const canNextPage = currentPage < pageCount - 1;
  return (
    <MotionBox
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <GlassMorphismCard mb={6}>
        {/* Header with search and filters */}
        <Flex
          p={6}
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "start", md: "center" }}
          wrap="wrap"
          gap={4}
        >
          {/* Search input */}
          <HStack spacing={4} flex={{ base: "1", md: "2" }} flexWrap="wrap">
            <VStack align="start" spacing={1} minW="250px">
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Search Payees
              </Text>
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by name, email, role or type"
                  borderRadius="md"
                />
              </InputGroup>
              <Text fontSize="xs" color="gray.500" mt={1} ml={1}>
                <Icon as={InfoIcon} boxSize={3} mr={1} />
                Type any keyword to search across all fields
              </Text>
            </VStack>
            <VStack align="start" spacing={1} minW="200px">
              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                Verification Status
              </Text>
              <Select
                size="sm"
                borderRadius="md"
                value={statusFilter || "all"}
                onChange={(e) => setStatusFilter && setStatusFilter(e.target.value)}
              >
                <option value="all">All</option>
                <option value="not_initiated">Not Initiated</option>
                <option value="pending">Pending</option>
                <option value="verified">Success</option>
                <option value="rejected">Rejected</option>
              </Select>            </VStack>
            <Button
              size="sm"
              colorScheme="orange"
              onClick={onRefresh}
              borderRadius="md"
              mt={6}
            >
              Search
            </Button>
          </HStack>

          {/* Title */}
          <HStack spacing={2} justify="flex-end">
            <Text fontSize="xl" fontWeight="bold">
              Payees
            </Text>
            <Badge
              colorScheme="orange"
              fontSize="sm"
              py={1}
              px={2}
              borderRadius="md"
            >
              {filteredPayees.length}
            </Badge>
          </HStack>
        </Flex>

        {/* Table */}
        <TableContainer>
          <Table variant="simple" color="gray.500">
            <Thead bg={theadBgColor}>
              <Tr>
                {payeeTableHeaders.map((item, index) => {
                  return (
                    <Th
                      key={index}
                      py={4}
                      px={6}
                      borderColor={borderColor}
                      textTransform="capitalize"
                      fontSize="xs"
                      fontWeight="bold"
                      letterSpacing="wider"
                      textAlign="center"
                    >
                      <Flex
                        justify="center"
                        align="center"
                        fontSize={{ sm: "10px", lg: "12px" }}
                        color="gray.400"
                      >
                        {item.Header}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            </Thead>
            <Tbody>
              {paginatedData.map((payee, index) => {
                return (
                  <MotionTr
                    key={payee.Id}
                    whileHover={{ backgroundColor: hoverBg }}
                    transition={{ duration: 0.2 }}
                  >
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {currentPage * pageSize + index + 1}
                      </Text>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Flex align="center" gap={3}>
                        <Avatar
                          size="sm"
                          name={`${payee?.FirstName} ${payee?.LastName}`}
                          bg={themeColor}
                          color="white"
                        />
                        <VStack spacing={0} align="start">
                          <Text
                            color={textColor}
                            fontSize="sm"
                            fontWeight="600"
                          >
                            {`${payee?.FirstName} ${payee?.LastName}`}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            ID: {payee?.Id}
                          </Text>
                        </VStack>
                      </Flex>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {payee?.Email}
                      </Text>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Text color={textColor} fontSize="sm" fontWeight="600">
                        {payee?.MobileNumber}
                      </Text>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Flex align="center" justify="center">
                        <TypeBadge type={payee?.Role} />
                      </Flex>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Flex align="center" justify="center">
                        <StatusBadge status={payee?.VerificationStatus} />
                      </Flex>
                    </Td>
                    <Td
                      textAlign="center"
                      py={4}
                      px={6}
                      borderColor={borderColor}
                    >
                      <Flex align="center" justify="center" gap={2}>
                        <Tooltip label="View Details" placement="top">
                          <IconButton
                            icon={<ViewIcon />}
                            colorScheme="blue"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewDetails(payee)}
                            borderRadius="full"
                            aria-label="View Details"
                          />
                        </Tooltip>

                        {/* Linked Account Actions for Verified Payees */}
                        {payee?.VerificationStatus === "verified" && (
                          <>
                            {!payee?.LinkedAccountId || payee?.LinkedAccountId === "" ? (
                              <Tooltip label="Create Linked Account" placement="top">
                                <IconButton
                                  icon={<FaPlus />}
                                  colorScheme="green"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleCreateLinkedAccount(payee)}
                                  isLoading={loadingActions[`${payee.Id}_create_linked`]}
                                  borderRadius="full"
                                  aria-label="Create Linked Account"
                                />
                              </Tooltip>
                            ) : (
                              <Tooltip label="View Linked Account Details" placement="top">
                                <IconButton
                                  icon={<FaLink />}
                                  colorScheme="green"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewLinkedAccount(payee)}
                                  isLoading={loadingActions[`${payee.Id}_view_linked`]}
                                  borderRadius="full"
                                  aria-label="View Linked Account Details"
                                />
                              </Tooltip>
                            )}
                          </>
                        )}

                        {/* Approval/Rejection Actions for Pending Payees */}
                        {payee?.VerificationStatus === "pending" && (
                          <>
                            <Tooltip label="Approve Payee" placement="top">
                              <IconButton
                                icon={<CheckIcon />}
                                colorScheme="green"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleVerifyPayee(payee)}
                                isLoading={loadingActions[`${payee.Id}_accept`]}
                                borderRadius="full"
                                aria-label="Approve Payee"
                              />
                            </Tooltip>
                            <Tooltip label="Reject Payee" placement="top">
                              <IconButton
                                icon={<CloseIcon />}
                                colorScheme="red"
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRejectPayee(payee)}
                                isLoading={loadingActions[`${payee.Id}_reject`]}
                                borderRadius="full"
                                aria-label="Reject Payee"
                              />
                            </Tooltip>
                          </>
                        )}
                      </Flex>
                    </Td>
                  </MotionTr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <Flex
          p={4}
          justify="space-between"
          align="center"
          borderTop="1px solid"
          borderColor={borderColor}
          flexWrap="wrap"
          gap={4}
        >
          <HStack>
            <Select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              size="sm"
              borderRadius="md"
              w="120px"
            >
              {[5, 8, 10, 20, 30].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </Select>
            <Text fontSize="sm" color="gray.500">
              Showing {Math.min(paginatedData.length, pageSize)} of{" "}
              {filteredPayees.length} results
            </Text>
          </HStack>

          <HStack spacing={2}>
            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => gotoPage(0)}
            >
              <ChevronLeftIcon boxSize={4} mr={1} />
              First
            </PaginationButton>

            <PaginationButton
              isDisabled={!canPreviousPage}
              onClick={() => previousPage()}
            >
              <ChevronLeftIcon boxSize={4} />
            </PaginationButton>

            {pageCount <= 5 ? (
              // If we have 5 or fewer pages, show all page buttons
              [...Array(pageCount)].map((_, i) => (
                <PaginationButton
                  key={i}
                  isActive={currentPage === i}
                  onClick={() => gotoPage(i)}
                >
                  {i + 1}
                </PaginationButton>
              ))
            ) : (
              // If we have more than 5 pages, show a subset
              <>
                {currentPage > 1 && (
                  <PaginationButton onClick={() => gotoPage(0)}>
                    1
                  </PaginationButton>
                )}

                {currentPage > 2 && <Text>...</Text>}

                {currentPage > 0 && (
                  <PaginationButton onClick={() => gotoPage(currentPage - 1)}>
                    {currentPage}
                  </PaginationButton>
                )}

                <PaginationButton
                  isActive
                  onClick={() => gotoPage(currentPage)}
                >
                  {currentPage + 1}
                </PaginationButton>

                {currentPage < pageCount - 1 && (
                  <PaginationButton onClick={() => gotoPage(currentPage + 1)}>
                    {currentPage + 2}
                  </PaginationButton>
                )}

                {currentPage < pageCount - 3 && <Text>...</Text>}

                {currentPage < pageCount - 2 && (
                  <PaginationButton onClick={() => gotoPage(pageCount - 1)}>
                    {pageCount}
                  </PaginationButton>
                )}
              </>
            )}

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => nextPage()}
            >
              <ChevronRightIcon boxSize={4} />
            </PaginationButton>

            <PaginationButton
              isDisabled={!canNextPage}
              onClick={() => gotoPage(pageCount - 1)}
            >
              Last
              <ChevronRightIcon boxSize={4} ml={1} />
            </PaginationButton>
          </HStack>
        </Flex>

        {/* Empty state */}
        {filteredPayees.length === 0 && <EmptyStateIllustration />}
      </GlassMorphismCard>

      {/* Payee Details Modal */}
      <PayeeDetailsModal
        isOpen={isDetailModalOpen}
        onClose={closeDetailModal}
        payee={selectedPayee}
      />

      {/* Linked Account Details Modal */}
      <LinkedAccountDetailsModal
        isOpen={isLinkedAccountModalOpen}
        onClose={closeLinkedAccountModal}
        accountData={linkedAccountData}
      />
    </MotionBox>
  );
};

export default PayeesTable;
